import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { getArtworkBySlug, artworkSrcSet } from "@/data/artworks";
import { ArtworkMeta } from "@/components/art/ArtworkMeta";
import { ArtworkEnquiry } from "@/components/art/ArtworkEnquiry";

export default function ArtworkDetail() {
  const { slug } = useParams();
  const artwork = getArtworkBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!artwork) return <Navigate to="/art" replace />;

  const scrollToEnquire = () => {
    document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid={`artwork-detail-${artwork.slug}`} className="bg-[#FBFAF6]">
      <section className="pt-12 lg:pt-20 pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Link
            to="/art"
            data-testid="back-to-collection-link"
            className="inline-block text-[11px] uppercase tracking-[0.24em] text-[#55524C] hover:text-[#2F2C28] transition-colors"
          >
            ← Back to Collection
          </Link>

          <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-7">
              <img
                src={`${artwork.imgBase}-1600.webp`}
                srcSet={artworkSrcSet(artwork)}
                sizes="(max-width: 1023px) calc(100vw - 48px), 780px"
                width={artwork.imgWidth}
                height={artwork.imgHeight}
                decoding="async"
                alt={artwork.alt}
                className="block w-full h-auto object-contain"
              />
            </div>

            <div className="lg:col-span-5 lg:sticky lg:top-[120px]">
              <h1
                className="serif text-[#2F2C28] leading-[1.15]"
                style={{ fontSize: "clamp(2rem, 3.5vw, 3.2rem)" }}
              >
                {artwork.title}
              </h1>
              <ArtworkMeta artwork={artwork} className="mt-6" />
              {artwork.artistNote && (
                <p className="mt-8 serif italic text-[17px] leading-[1.85] text-[#2F2C28]">{artwork.artistNote}</p>
              )}
              <button
                type="button"
                onClick={scrollToEnquire}
                data-testid="detail-enquire-btn"
                className="btn-primary mt-10 inline-flex"
              >
                Enquire to Acquire
              </button>
            </div>
          </div>

          {artwork.contextImage && (
            <div className="mt-24 lg:mt-32 max-w-[1000px] mx-auto">
              <img src={artwork.contextImage} alt={`${artwork.title} shown in an interior setting`} loading="lazy" decoding="async" className="block w-full h-auto object-contain" />
            </div>
          )}
          {artwork.detailImage && (
            <div className="mt-16 max-w-[1000px] mx-auto">
              <img src={artwork.detailImage} alt={`${artwork.title}, detail photograph`} loading="lazy" decoding="async" className="block w-full h-auto object-contain" />
            </div>
          )}
        </div>
      </section>

      <section id="enquire" className="py-24 lg:py-32 bg-[#F2EFE5] border-t border-[#E7E2D6] scroll-mt-24">
        <div className="max-w-[640px] mx-auto px-6 sm:px-10">
          <div className="label-eyebrow text-center">Enquire to Acquire</div>
          <div className="mt-12">
            <ArtworkEnquiry artwork={artwork} />
          </div>
        </div>
      </section>
    </div>
  );
}
